import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowLeft, ArrowRight, Loader2, AlertCircle } from 'lucide-react'
import { useCreateSeries, useGenerateSeriesName } from '../../api/series'
import { useAuth } from '../../hooks/use-auth'
import { isValidEpisodeDuration, DEFAULT_EPISODE_DURATION_MINUTES } from '../../lib/plans'
import type { CreateSeriesRequest, FollowedShow, SeriesUserProfile } from '../../types/series'
import { UseCaseStep, type UseCase } from './steps/use-case-step'
import { InterestsStep } from './steps/interests-step'
import { MaterialsStep, type MaterialItem } from './steps/materials-step'
import { NegativeKeywordsStep } from './steps/negative-keywords-step'
import { ScheduleStep } from './steps/schedule-step'
import { StyleStep } from './steps/style-step'
import { VoiceStep } from './steps/voice-step'
import { NameStep } from './steps/name-step'
import { ConfirmationStep } from './steps/confirmation-step'

const LAST_STEP = 9 // internal step numbering

// Personalized defaults based on use case
function getPersonalizedDefaults(useCase: UseCase): Partial<WizardData> {
  switch (useCase) {
    case 'busy-professional':
      return {
        frequency: 'weekdays',
        episodeDurationMinutes: 10,
        deliveryTimeLocal: 420, // 7:00 AM - perfect for commute
        style: 'morning-briefing',
      }
    case 'student-learner':
      return {
        frequency: 'weekdays',
        episodeDurationMinutes: 15,
        deliveryTimeLocal: 540, // 9:00 AM
        style: 'deep-dive',
      }
    case 'curious-explorer':
      return {
        frequency: 'weekly',
        episodeDurationMinutes: 20,
        deliveryTimeLocal: 600, // 10:00 AM - weekend listening
        style: 'storyteller',
      }
    default:
      return {}
  }
}

// Sonic 3.0 Emotive Defaults
const DEFAULT_HOST_A = 'c961b81c-a935-4c17-bfb3-ba2239de8c2f'
const DEFAULT_HOST_B = '6ccbfb76-1fc6-48f7-b71d-91ac6298247b'

interface WizardData {
  useCase: UseCase | null
  interests: string
  userProfile: SeriesUserProfile | null
  materials: MaterialItem[]
  followedShows: FollowedShow[]
  negativeKeywords: string
  frequency: 'daily' | 'weekdays' | 'weekly' | 'biweekly'
  episodeDurationMinutes: number
  deliveryTimeLocal: number
  timezone: string
  style: 'morning-briefing' | 'two-friends' | 'deep-dive' | 'storyteller'
  hostAVoiceId: string
  hostBVoiceId: string
  languageIso: string
  name: string
  description: string
}

export function SeriesCreateWizard() {
  const navigate = useNavigate()
  const createSeries = useCreateSeries()
  const generateName = useGenerateSeriesName()
  const { user } = useAuth()
  const userPlanDuration = isValidEpisodeDuration(user?.episodeDurationMinutes)
    ? user!.episodeDurationMinutes!
    : DEFAULT_EPISODE_DURATION_MINUTES
  const [step, setStep] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<WizardData>({
    useCase: null,
    interests: '',
    userProfile: null,
    materials: [],
    followedShows: [],
    negativeKeywords: '',
    frequency: 'weekly',
    episodeDurationMinutes: Math.min(10, userPlanDuration),
    deliveryTimeLocal: 420,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    style: 'morning-briefing',
    hostAVoiceId: DEFAULT_HOST_A,
    hostBVoiceId: DEFAULT_HOST_B,
    languageIso: 'en',
    name: '',
    description: '',
  })

  const hasMaterialsStep = data.useCase === 'student-learner'
  
  // Calculate total steps: base is 8, +1 for materials
  const totalStepsForDisplay = hasMaterialsStep ? 9 : 8

  // For progress display, compute effective step based on which steps are active
// Steps: 1-UseCase, 2-Interests, 3-Materials(optional), 4-Avoid, 5-Schedule, 6-Vibe, 7-Voices, 8-Name, 9-Confirm
  const effectiveStep = (() => {
    let effective = step
    // If we skipped materials (step 3), subtract 1 for steps 4+
    if (!hasMaterialsStep && step >= 4) effective -= 1
    return effective
  })()

  const updateData = (partial: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...partial }))
  }

  const canProceed = (): boolean => {
    switch (step) {
      case 1:
        return data.useCase !== null
      case 2:
        return data.interests.trim().length >= 10 // Interests managed by chat
      case 3:
        return true // materials optional
      case 4:
        return true // negative kw optional
      case 5:
        return true // has defaults
      case 6:
        return true // has default
      case 7:
        return true // voices have defaults
      case 8:
        return data.name.trim().length > 0
      case 9:
        return true
      default:
        return false
    }
  }

  const handleNext = async () => {
    // When moving from use case step, apply personalized defaults
    if (step === 1 && data.useCase) {
      const defaults = getPersonalizedDefaults(data.useCase)
      // Cap personalized duration to the user's plan limit.
      if (defaults.episodeDurationMinutes !== undefined) {
        defaults.episodeDurationMinutes = Math.min(defaults.episodeDurationMinutes, userPlanDuration)
      }
      // Clear materials if switching away from student use case
      if (data.useCase !== 'student-learner' && data.materials.length > 0) {
        updateData({ ...defaults, materials: [] })
      } else {
        updateData(defaults)
      }
      setStep(2)
      return
    }

    // Step 2 (interests) -> materials or avoid
    if (step === 2) {
      if (hasMaterialsStep) {
        setStep(3)
      } else {
        setStep(4)
      }
      return
    }

    // Skip materials step for non-student use cases (step 3 -> step 4)
    if (step === 3 && !hasMaterialsStep) {
      setStep(4)
      return
    }

    if (step === 7 && !data.name) {
      // Auto-generate name when moving from voices to name step
      setError(null)
      generateName.mutate({ interests: data.interests, languageIso: data.languageIso }, {
        onSuccess: (result) => {
          updateData({ name: result.name, description: result.description })
          setStep(8)
        },
        onError: (err) => {
          const errorMessage = err instanceof Error ? err.message : 'Failed to generate name. Please try again.'
          setError(errorMessage)
        },
      })
      return
    }

    if (step < LAST_STEP) {
      setStep(step + 1)
    }
  }

  // Handle proceeding from interests chat step
  const handleInterestsComplete = () => {
    if (hasMaterialsStep) {
      setStep(3)
    } else {
      setStep(4)
    }
  }

  const handleBack = () => {
    // Going back from materials step (3) or avoid step (4)
    if (step === 4 && !hasMaterialsStep) {
      setStep(2)
      return
    }
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const handleCreate = async () => {
    setError(null)
    const request: CreateSeriesRequest = {
      interests: data.interests,
      negativeKeywords: data.negativeKeywords || undefined,
      userProfile: data.userProfile || undefined,
      followedShows: data.followedShows.length > 0 ? data.followedShows : undefined,
      materials: data.materials.length > 0 ? data.materials.map(m => ({
        url: m.url,
        name: m.name,
        type: m.type,
        sizeBytes: m.sizeBytes,
      })) : undefined,
      frequency: data.frequency,
      episodeDurationMinutes: data.episodeDurationMinutes,
      deliveryTimeLocal: data.deliveryTimeLocal,
      timezone: data.timezone,
      style: data.style,
      hostAVoiceId: data.hostAVoiceId,
      hostBVoiceId: data.hostBVoiceId,
      languageIso: data.languageIso,
      name: data.name,
      description: data.description,
    }

    createSeries.mutate(request, {
      onSuccess: (series) => {
        navigate(`/series/${series.id}`, { state: { justCreated: true } })
      },
      onError: (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create series. Please try again.'
        setError(errorMessage)
      },
    })
  }

  // Build step labels dynamically based on active steps
  const stepLabels = (() => {
    const labels = ['Goal', 'Interests']
    if (hasMaterialsStep) labels.push('Materials')
    labels.push('Avoid', 'Schedule', 'Vibe', 'Voices & lang', 'Name', 'Confirm')
    return labels
  })()

  // Returns "Skip" for optional steps with no data, "Continue" otherwise
  const getButtonLabel = (): string => {
    if (step === 3 && hasMaterialsStep && data.materials.length === 0) return 'Skip'
    if (step === 4 && !data.negativeKeywords.trim()) return 'Skip'
    return 'Continue'
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      {/* Progress bar */}
      <div className="mb-10">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground/60">
            Step {effectiveStep} of {totalStepsForDisplay}
          </span>
          <span className="text-sm font-semibold text-foreground/60">{stepLabels[effectiveStep - 1]}</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/5">
          <motion.div
            className="h-full rounded-full bg-brand-500"
            initial={false}
            animate={{ width: `${(effectiveStep / totalStepsForDisplay) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Step content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {step === 1 && <UseCaseStep value={data.useCase} onChange={(v) => updateData({ useCase: v })} />}
          {step === 2 && (
            <InterestsStep
              value={data.interests}
              useCase={data.useCase}
              onChange={(v) => updateData({ interests: v.interests, userProfile: v.userProfile || null })}
              onComplete={handleInterestsComplete}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <MaterialsStep
              materials={data.materials}
              useCase={data.useCase}
              onChange={(v) => updateData({ materials: v })}
            />
          )}
          {step === 4 && (
            <NegativeKeywordsStep
              value={data.negativeKeywords}
              useCase={data.useCase}
              onChange={(v) => updateData({ negativeKeywords: v })}
            />
          )}
          {step === 5 && (
            <ScheduleStep
              frequency={data.frequency}
              episodeDurationMinutes={data.episodeDurationMinutes}
              deliveryTimeLocal={data.deliveryTimeLocal}
              timezone={data.timezone}
              useCase={data.useCase}
              maxDurationMinutes={userPlanDuration}
              onFrequencyChange={(v) => updateData({ frequency: v })}
              onDurationChange={(v) => updateData({ episodeDurationMinutes: v })}
              onDeliveryTimeChange={(v) => updateData({ deliveryTimeLocal: v })}
              onTimezoneChange={(v) => updateData({ timezone: v })}
            />
          )}
          {step === 6 && (
            <StyleStep value={data.style} useCase={data.useCase} onChange={(v) => updateData({ style: v })} />
          )}
          {step === 7 && (
            <VoiceStep
              hostAVoiceId={data.hostAVoiceId}
              hostBVoiceId={data.hostBVoiceId}
              languageIso={data.languageIso}
              onHostAChange={(v) => updateData({ hostAVoiceId: v })}
              onHostBChange={(v) => updateData({ hostBVoiceId: v })}
              onLanguageChange={(v) => updateData({ languageIso: v })}
            />
          )}
          {step === 8 && (
            <NameStep
              name={data.name}
              description={data.description}
              onNameChange={(v) => updateData({ name: v })}
              onDescriptionChange={(v) => updateData({ description: v })}
              isGenerating={generateName.isPending}
            />
          )}
          {step === 9 && <ConfirmationStep data={data} />}
        </motion.div>
      </AnimatePresence>

      {/* Error display */}
      {error && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-danger-200 bg-danger-50 p-4">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-danger-600" />
          <div>
            <p className="text-sm font-medium text-danger-800">Something went wrong</p>
            <p className="mt-1 text-sm text-danger-700">{error}</p>
          </div>
        </div>
      )}

      {/* Navigation buttons - hidden for interests step which has its own navigation */}
      {step !== 2 && (
        <div className="mt-12 flex items-center justify-between">
          <button
            onClick={step === 1 ? () => navigate('/series') : handleBack}
            className="flex cursor-pointer items-center gap-2 rounded-full px-5 py-3 text-[15px] font-bold text-foreground/60 transition-colors hover:bg-black/5 hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < LAST_STEP ? (
            <button
              onClick={handleNext}
              disabled={!canProceed() || generateName.isPending}
              className="flex cursor-pointer items-center gap-2 rounded-full bg-brand-600 px-7 py-3 text-[15px] font-bold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-secondary-300 disabled:opacity-50"
            >
              {generateName.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Generating name...
                </>
              ) : (
                <>
                  {getButtonLabel()}
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={createSeries.isPending}
              className="flex cursor-pointer items-center gap-2 rounded-full bg-brand-600 px-7 py-3 text-[15px] font-bold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-secondary-300 disabled:opacity-50"
            >
              {createSeries.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create series & generate first episode'
              )}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
